import React from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
// import Skeleton from "@material-ui/lab/Skeleton";
import Map from "ol/Map";
import { defaults as defaultControls } from "ol/control";
import { defaults as defaultInteractions } from "ol/interaction";
import { getCenter, Extent } from "ol/extent";
import Projection from "ol/proj/Projection";
import View from "ol/View";
import clsx from "clsx";
import ImageLayer from "ol/layer/Image";
import ImageStatic from "ol/source/ImageStatic";
import OverviewMap from "ol/control/OverviewMap";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";

import { LapiService } from "../../lib/lapi";
import useAuth from "../../hooks/useAuth";
import IconButton from "@material-ui/core/IconButton";

const useStyles = makeStyles((theme: Theme) => ({
  imageViewerContainer: {
    height: "100%",
    width: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    color: theme.palette.imageControl.main,
    overflow: "hidden",
  },
  zoomInControl: {
    position: "absolute",
    top: 16,
    right: 8,
    borderRadius: "50%",
    backgroundColor: theme.palette.common.white,
    "& :first-child": {
      padding: 0,
    },
  },
  zoomOutControl: {
    position: "absolute",
    top: 32 + 16,
    right: 8,
    borderRadius: "50%",
    backgroundColor: theme.palette.common.white,
    "& :first-child": {
      padding: 0,
    },
  },
  olCustomOverviewMap: {
    "&, &.ol-uncollapsible, &:hover": {
      bottom: 0,
      right: 0,
      top: "auto",
      left: "auto",
      borderRadius: 0,
      backgroundColor: "transparent",
    },
    "&:not(.ol-collapsed)": {
      backgroundColor: "transparent",
    },
    "& .ol-overviewmap-map": {
      width: 240,
      height: 180,
      border: `thin solid ${theme.palette.common.white}`,
    },
    "& .ol-overviewmap-box": {
      border: `medium solid ${theme.palette.common.white}`,
    },
    "&:not(.ol-collapsed) button": {
      bottom: 1,
      right: 1,
      top: "auto",
      left: "auto",
    },
    "& button, & button:hover, & button:focus": {
      backgroundColor: "initial",
      fontSize: "2.1875rem",
      cursor: "pointer",
    },
  },
}));

const overviewMapIconColor = "white";
const overviewMapIconWidth = "32";
const overviewMapIconHeight = "32";
const overviewMapIcon = `<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="${overviewMapIconWidth}" height="${overviewMapIconHeight}" viewBox="0 0 24 24"><path fill="${overviewMapIconColor}" d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z" /></svg>`;

export type LapiImgProps = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
> & {
  size?: number;
  zoom?: boolean;
  target?: string;
};

function LapiImg(props: LapiImgProps) {
  const { className, src, size, zoom, target, alt = "", ...other } = props;
  const classes = useStyles();
  const { token } = useAuth();
  const [innerSrc, setInnerSrc] = React.useState<string>();
  // const [loading, setLoading] = React.useState(true);
  const imageViewerRef = React.useRef(
    new Map({
      controls: defaultControls({
        attribution: false,
        rotate: false,
        zoom: false,
      }),
      interactions: defaultInteractions({
        altShiftDragRotate: false,
        pinchRotate: false,
      }),
    })
  );

  React.useEffect(() => {
    if (src === undefined || token === undefined) return;

    let map = imageViewerRef.current;
    map.setTarget(target ? target : "imageViewerControl");
    let mounted = true;
    // setLoading(true);

    LapiService.getLapiImage(src, token, size)
      .then((lapiImage) => {
        if (mounted) setInnerSrc(lapiImage);
        if (zoom) {
          LapiService.getLapiImage(src, token)
            .then((lapiImage) => {
              if (mounted) setInnerSrc(lapiImage);
            })
            .catch((error) => {
              if (process.env.NODE_ENV !== "production") console.error(error);
            });
        }
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") console.error(error);
      })
      .finally(() => {
        // if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [size, src, target, token, zoom]);

  // When the image changes, do the required side effects.
  React.useEffect(() => {
    if (innerSrc === undefined || !zoom) return;
    let mounted = true;

    const map = imageViewerRef.current;
    let layer: ImageLayer | null = null;
    let overviewMapControl: OverviewMap | null = null;

    const image = new Image();
    image.onload = () => {
      if (!mounted) return;
      const extent: Extent = [0, 0, image.width, image.height];
      const projection = new Projection({
        code: "pano-image",
        units: "pixels",
        extent,
      });
      const view = new View({
        projection,
        center: getCenter(extent),
        zoom: 0,
        extent,
        showFullExtent: true,
      });
      const source = new ImageStatic({
        url: innerSrc,
        projection,
        imageExtent: extent,
      });
      layer = new ImageLayer({ source });
      const overviewMapControlLabel = document.createElement("span");
      overviewMapControlLabel.innerHTML = overviewMapIcon;
      overviewMapControl = new OverviewMap({
        className: clsx("ol-overviewmap", classes.olCustomOverviewMap),
        collapseLabel: overviewMapControlLabel,
        label: overviewMapControlLabel,
        layers: [new ImageLayer({ source })],
      });

      map.setView(view);
      map.addLayer(layer);
      map.addControl(overviewMapControl);
    };

    image.src = innerSrc;

    return () => {
      mounted = false;
      if (layer !== null) map.removeLayer(layer);
      if (overviewMapControl !== null) map.removeControl(overviewMapControl);
    };
  }, [classes.olCustomOverviewMap, innerSrc, zoom]);

  const handleZoomInButtonClick = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const map = imageViewerRef.current;

      const view = map.getView();
      const prevZoom = view.getZoom();
      const nextZoom = view.getConstrainedZoom(
        prevZoom !== undefined ? prevZoom + 1 : undefined
      );

      if (view.getAnimating()) view.cancelAnimations();

      view.animate({
        zoom: nextZoom,
        duration: 250,
      });
    },
    []
  );

  const handleZoomOutButtonClick = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const map = imageViewerRef.current;

      const view = map.getView();
      const prevZoom = view.getZoom();
      const nextZoom = view.getConstrainedZoom(
        prevZoom !== undefined ? prevZoom - 1 : undefined
      );

      if (view.getAnimating()) view.cancelAnimations();

      view.animate({
        zoom: nextZoom,
        duration: 250,
      });
    },
    []
  );

  return (
    <>
      {zoom ? (
        <div className={classes.imageViewerContainer}>
          <div
            className={className}
            id={target ? target : "imageViewerControl"}
          />
          <div className={classes.zoomInControl}>
            <IconButton color="inherit" onClick={handleZoomInButtonClick}>
              <AddIcon />
            </IconButton>
          </div>
          <div className={classes.zoomOutControl}>
            <IconButton color="inherit" onClick={handleZoomOutButtonClick}>
              <RemoveIcon />
            </IconButton>
          </div>
        </div>
      ) : (
        <img className={className} src={innerSrc} alt={alt} {...other} />
      )}
    </>
  );
}

export default LapiImg;
